import type { LedgerEvent } from './types';
import { buildCsvFromRows } from './csvExport';

function formatAmount(amt: LedgerEvent['amount']): string {
  return amt != null && typeof (amt as { toFixed?: (n: number) => string }).toFixed === 'function'
    ? (amt as { toFixed: (n: number) => string }).toFixed(8)
    : String(amt ?? '');
}

function formatEur(v: LedgerEvent['eurValue']): string {
  return v != null && typeof (v as { toFixed?: (n: number) => string }).toFixed === 'function'
    ? (v as { toFixed: (n: number) => string }).toFixed(2)
    : '';
}

/** Fixed schema for full-ledger troubleshooting export (must match column count per row). */
export const FULL_LEDGER_CSV_HEADERS = [
  'timestamp_utc',
  'source',
  'kind',
  'asset',
  'amount',
  'eur_value',
  'classification',
  'confidence',
  'ref',
  'from_address',
  'to_address',
  'primary_wallet',
  'primary_wallet_label',
  'review_flags',
  'explanation',
  'fi_tax_category',
  'fi_subreason',
  'lot_id',
  'origin_lot_id',
  'origin_timestamp_ms',
  'origin_source_ref',
  'origin_wallet',
  'current_wallet',
  'basis_continuity_ref',
  'wallet_continuity_kind',
  'acquisition_provenance',
  'acquisition_provenance_flag',
] as const;

/**
 * Full ledger CSV — one row per event, fixed columns (audit / debug).
 */
export function buildFullLedgerCsv(events: readonly LedgerEvent[]): string {
  const rows: string[][] = [];
  for (const e of events) {
    const iso = new Date(e.ts).toISOString();
    const amt = formatAmount(e.amount);
    const eur = formatEur(e.eurValue);
    const flags = Array.isArray(e.reviewFlags) ? e.reviewFlags.join(';') : '';
    rows.push([
      iso,
      e.source,
      e.kind,
      e.asset,
      amt,
      eur,
      e.classification ?? '',
      e.confidence ?? '',
      e.ref ?? '',
      e.fromAddress ?? '',
      e.toAddress ?? '',
      e.primaryWalletAddress ?? '',
      e.primaryWalletLabel ?? '',
      flags,
      e.explanation ?? '',
      e.fiTaxCategory ?? '',
      e.fiSubreason ?? '',
      e.lotId ?? '',
      e.originLotId ?? '',
      e.originTimestamp != null ? String(e.originTimestamp) : '',
      e.originSourceRef ?? '',
      e.originWallet ?? '',
      e.currentWallet ?? '',
      e.basisContinuityRef ?? '',
      e.walletContinuityKind ?? '',
      e.acquisitionProvenance ?? '',
      e.acquisitionProvenanceFlag ?? '',
    ]);
  }
  return buildCsvFromRows([...FULL_LEDGER_CSV_HEADERS], rows);
}
